import React, { useState, useEffect } from 'react';
import { Clock, Plus, CheckCircle, AlertTriangle } from 'lucide-react';
import { Reminder, ReminderFormData } from '../../types/Reminder';
import { apiClient } from '../../config/api';
import AdminTable from '../shared/AdminTable';
import AdminHeader from '../shared/AdminHeader';
import ReminderForm from './ReminderForm';
import { reminderTableConfig } from './ReminderTableConfig';

const EnhancedReminderManagement: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingReminder, setEditingReminder] = useState<Reminder | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Load reminders and populate dynamic filter options
  useEffect(() => {
    const loadReminders = async () => {
      try {
        const data = await apiClient.get('/api/admin/reminders');

        // Update category filter options dynamically
        const categories = Array.from(new Set(
          data
            .map((reminder: Reminder) => reminder.category)
            .filter((category: string | undefined) => category && category.trim())
        ));

        reminderTableConfig.filterOptions.category = categories.map(category => ({
          value: category as string,
          label: category as string
        }));
      } catch (error) {
        console.error('Failed to load reminders:', error);
      }
    };

    loadReminders();
  }, [refreshTrigger]);

  const handleCreateReminder = () => {
    setEditingReminder(null);
    setShowForm(true);
  };

  const handleEditReminder = (reminder: Reminder) => {
    setEditingReminder(reminder);
    setShowForm(true);
  };

  const handleDeleteReminder = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this reminder?')) {
      return;
    }

    try {
      setIsLoading(true);
      await apiClient.delete(`/api/admin/reminders/${id}`);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Failed to delete reminder:', error);
      alert('Failed to delete reminder. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleComplete = async (reminder: Reminder) => {
    try {
      setIsLoading(true);
      if (!reminder.completed) {
        // Mark as completed
        await apiClient.patch(`/api/admin/reminders/${reminder.id}/complete`);
      } else {
        // Update to mark as incomplete
        const updatedData: ReminderFormData = {
          title: reminder.title,
          description: reminder.description,
          dueDateTime: reminder.dueDateTime,
          priority: reminder.priority,
          category: reminder.category,
          completed: false,
          publicVisible: reminder.publicVisible,
          location: reminder.location,
          durationMinutes: reminder.durationMinutes
        };
        await apiClient.put(`/api/admin/reminders/${reminder.id}`, updatedData);
      }
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Failed to toggle reminder completion:', error);
      alert('Failed to update reminder. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = async (formData: ReminderFormData) => {
    try {
      setIsLoading(true);

      if (editingReminder) {
        // Update existing reminder
        await apiClient.put(`/api/admin/reminders/${editingReminder.id}`, formData);
      } else {
        // Create new reminder
        await apiClient.post('/api/admin/reminders', formData);
      }

      setShowForm(false);
      setEditingReminder(null);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Failed to save reminder:', error);
      alert('Failed to save reminder. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingReminder(null);
  };

  const customActions = [
    {
      label: 'Complete',
      icon: CheckCircle,
      onClick: handleToggleComplete,
      className: 'text-green-600 hover:text-green-800',
      condition: (reminder: Reminder) => !reminder.completed
    },
    {
      label: 'Mark Incomplete',
      icon: AlertTriangle,
      onClick: handleToggleComplete,
      className: 'text-orange-600 hover:text-orange-800',
      condition: (reminder: Reminder) => reminder.completed
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-8 space-y-6">
        {/* Header */}
        <AdminHeader
          title="Reminder Management"
          subtitle="Create and manage reminders and tasks for the chorus"
          icon={<Clock size={32} />}
        />

        <div className="flex justify-end">
          <button
            onClick={handleCreateReminder}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2"
          >
            <Plus size={20} />
            Create Reminder
          </button>
        </div>

        {/* Admin Table */}
        <AdminTable<Reminder>
          onView={handleEditReminder}
          onEdit={handleEditReminder}
          onDelete={handleDeleteReminder}
          config={reminderTableConfig}
          isLoading={isLoading}
          refreshTrigger={refreshTrigger}
        />

        {showForm && (
          <ReminderForm
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
            initialData={editingReminder || undefined}
            isLoading={isLoading}
          />
        )}
      </div>
    </div>
  );
};

export default EnhancedReminderManagement;
